/**
 * Billing-related utility functions and constants
 */

/** Default trial period duration in days */
export const TRIAL_PERIOD_DAYS = 14

/** Number of milliseconds in a day */
export const DAY_IN_MS = 24 * 60 * 60 * 1000

/** DeepSource sales contact URL */
export const SALES_URL = 'https://deepsource.com/contact/sales'

// V2 (unified) plan slug constants
export const V2_SLUG_OPEN_SOURCE = 'uos'
export const V2_SLUG_TRIAL = 'utr'
export const V2_SLUG_TEAM = 'ut'
export const V2_SLUG_ENTERPRISE = 'uen'

const V2_SLUGS = new Set([V2_SLUG_OPEN_SOURCE, V2_SLUG_TRIAL, V2_SLUG_TEAM, V2_SLUG_ENTERPRISE])

/**
 * Extract slug string from subscribed_plan_info, which can be a string or { slug: string } object.
 */
function extractSlug(planInfo: unknown): string | undefined {
  if (!planInfo) return undefined
  if (typeof planInfo === 'object' && planInfo !== null && 'slug' in planInfo) {
    return (planInfo as Record<string, unknown>).slug as string | undefined
  }
  if (typeof planInfo === 'string') return planInfo
  return undefined
}

/**
 * Checks if a plan is a unified trial plan based on its slug
 * @param planInfo - The plan info object to check
 * @returns true if the plan is a unified trial plan, false otherwise
 */
export function isUnifiedTrialPlan(planInfo: unknown): boolean {
  const slug = extractSlug(planInfo)
  return slug === 'utr' || slug === 'trial'
}

/**
 * Returns the image path for a payment card brand.
 * Falls back to an unknown card image if the brand is not recognized.
 */
export function getCardImage(brand: string): string {
  try {
    return require(`~/assets/images/payments/${brand}.png`)
  } catch {
    return require('~/assets/images/payments/unknown.png')
  }
}

/** Convert cents (integer) to dollars (number). */
export function centsToDollars(cents: number): number {
  return cents / 100
}

/** Convert a dollar string to cents (integer). Returns 0 for invalid input. */
export function dollarsToCents(dollars: string): number {
  const parsed = parseFloat(dollars)
  return isFinite(parsed) ? Math.round(parsed * 100) : 0
}

/**
 * Returns true if the plan is a V2 (unified billing) plan.
 */
export function isUnifiedBillingPlan(planInfo: unknown): boolean {
  const slug = extractSlug(planInfo)
  return slug !== undefined && V2_SLUGS.has(slug)
}

/**
 * Returns true for Business/Enterprise-tier plans on both V1 and V2 billing.
 * V1: 'enterprise', 'premium'
 * V2: 'ut' (team), 'uen' (enterprise)
 */
export function isBusinessOrEnterprisePlan(planInfo: unknown): boolean {
  const slug = extractSlug(planInfo)
  if (!slug) return false
  return (
    slug === 'enterprise' ||
    slug === 'premium' ||
    slug === V2_SLUG_TEAM ||
    slug === V2_SLUG_ENTERPRISE
  )
}

/**
 * Returns true only for Enterprise-tier plans on both V1 and V2 billing.
 * Used for SSO gating.
 * V1: 'enterprise'
 * V2: 'uen'
 */
export function isEnterprisePlan(planInfo: unknown): boolean {
  const slug = extractSlug(planInfo)
  return slug === 'enterprise' || slug === V2_SLUG_ENTERPRISE
}

/**
 * Returns true for free-tier plans on both V1 and V2 billing.
 * V1: 'free'
 * V2: 'uos' (open source)
 */
export function isFreePlan(planInfo: unknown): boolean {
  const slug = extractSlug(planInfo)
  return slug === 'free' || slug === V2_SLUG_OPEN_SOURCE
}
